import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Table, Button, Space, Modal, Form, Input, Select, message,
    Card, Row, Col, Tag, Switch, Tooltip, Popconfirm, Empty
} from 'antd';
import {
    PlusOutlined, ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined,
    DeleteOutlined, EditOutlined, SyncOutlined, CodeOutlined
} from '@ant-design/icons';
import hostService from '../../../shared/services/hostService';

const { Option } = Select;
const { TextArea } = Input;

const ServiceManager = ({ containerId: propContainerId, embedded = false }) => {
    const { t } = useTranslation();
    const [containers, setContainers] = useState([]);
    const [selectedContainer, setSelectedContainer] = useState(propContainerId || null);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (embedded && propContainerId) {
            setSelectedContainer(propContainerId);
            return;
        }
        loadContainers();
        const urlParams = new URLSearchParams(window.location.search);
        const containerIdParam = urlParams.get('container_id');
        if (containerIdParam) {
            setSelectedContainer(parseInt(containerIdParam));
        }
    }, [propContainerId, embedded]);

    useEffect(() => {
        if (selectedContainer) {
            loadServices();
        }
    }, [selectedContainer]);

    const loadContainers = async () => {
        try {
            const response = await hostService.getContainers();
            if (response.success) {
                setContainers(response.data || []);
            }
        } catch (error) {
            message.error(t('Failed to retrieve containers'));
        }
    };

    const loadServices = async () => {
        if (!selectedContainer) return;
        setLoading(true);
        try {
            const response = await hostService.getServices(selectedContainer);
            if (response.success) {
                setServices(response.data || []);
            } else {
                message.error(response.error || t('Failed to retrieve services'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve services'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        form.resetFields();
        form.setFieldsValue({
            user: 'hostmanager',
            working_dir: '/var/www/html',
            auto_restart: true
        });
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const response = await hostService.createService({
                container_id: selectedContainer,
                ...values
            });

            if (response.success) {
                message.success(t('Service created'));
                setModalVisible(false);
                loadServices();
            } else {
                message.error(response.error || t('Creation failed'));
            }
        } catch (error) {
            message.error(t('Creation failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleStart = async (service) => {
        try {
            const response = await hostService.startService(selectedContainer, service.name);
            if (response.success) {
                message.success(t('Service started'));
                loadServices();
            } else {
                message.error(response.error || t('Failed to start'));
            }
        } catch (error) {
            message.error(t('Failed to start'));
        }
    };

    const handleStop = async (service) => {
        try {
            const response = await hostService.stopService(selectedContainer, service.name);
            if (response.success) {
                message.success(t('Service stopped'));
                loadServices();
            } else {
                message.error(response.error || t('Failed to stop'));
            }
        } catch (error) {
            message.error(t('Failed to stop'));
        }
    };

    const handleRestart = async (service) => {
        try {
            const response = await hostService.restartService(selectedContainer, service.name);
            if (response.success) {
                message.success(t('Service restarted'));
                loadServices();
            } else {
                message.error(response.error || t('Restart failed'));
            }
        } catch (error) {
            message.error(t('Restart failed'));
        }
    };

    const handleDelete = async (service) => {
        try {
            const response = await hostService.deleteService(selectedContainer, service.name);
            if (response.success) {
                message.success(t('Service deleted'));
                loadServices();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'running': { color: 'success', text: t('Running') },
            'stopped': { color: 'default', text: t('Stopped') },
            'starting': { color: 'processing', text: t('Starting') },
            'stopping': { color: 'warning', text: t('Stopping') },
            'error': { color: 'error', text: t('Error') },
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: t('Service name'),
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: t('Command'),
            dataIndex: 'command',
            key: 'command',
            ellipsis: true,
            render: (command) => (
                <Tooltip title={command}>
                    <code style={{ fontSize: 12 }}>{command}</code>
                </Tooltip>
            ),
        },
        {
            title: t('Working directory'),
            dataIndex: 'working_dir',
            key: 'working_dir',
            width: 180,
            ellipsis: true,
        },
        {
            title: t('Run as user'),
            dataIndex: 'user',
            key: 'user',
            width: 100,
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: t('Automatic restart'),
            dataIndex: 'auto_restart',
            key: 'auto_restart',
            width: 100,
            render: (autoRestart) => (
                <Tag color={autoRestart ? 'blue' : 'default'}>
                    {autoRestart ? t('Yes') : t('No')}
                </Tag>
            ),
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 200,
            render: (_, record) => (
                <Space>
                    {record.status !== 'running' && (
                        <Tooltip title={t('Start')}>
                            <Button
                                type="link"
                                size="small"
                                icon={<PlayCircleOutlined />}
                                onClick={() => handleStart(record)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'running' && (
                        <Tooltip title={t('Stop')}>
                            <Button
                                type="link"
                                size="small"
                                icon={<PauseCircleOutlined />}
                                onClick={() => handleStop(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={t('Restart')}>
                        <Button
                            type="link"
                            size="small"
                            icon={<SyncOutlined />}
                            onClick={() => handleRestart(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('Delete this service?')}
                        description={t('The service will stop running after deletion')}
                        onConfirm={() => handleDelete(record)}
                        okText={t('Confirm')}
                        cancelText={t('Cancel')}
                    >
                        <Tooltip title={t('Delete')}>
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {!embedded && <h2>{t('Background services')}</h2>}
            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    {!embedded && (
                        <Col span={8}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder={t('Select a container')}
                                value={selectedContainer}
                                onChange={setSelectedContainer}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {containers.map(c => (
                                    <Option key={c.id} value={c.id}>
                                        {c.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                    <Col span={embedded ? 24 : 16}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                                disabled={!selectedContainer}
                            >
                                {t('Create service')}
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadServices}
                                disabled={!selectedContainer}
                            >
                                {t('Refresh')}
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={services}
                    rowKey="name"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? ' ' : (
                            <Empty description={selectedContainer ? t('No background services yet. Click "Create service" to add one.') : t('Please select a container first')} />
                        ),
                    }}
                    pagination={false}
                />
            </Card>

            {/* 创建服务Modal */}
            <Modal
                title={t('Create background service')}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={submitting}
                okText={t('Create')}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        label={t('Service name')}
                        rules={[
                            { required: true, message: t('Enter a service name') },
                            { pattern: /^[a-zA-Z0-9_-]+$/, message: t('Only letters, numbers, underscores, and hyphens are allowed') }
                        ]}
                    >
                        <Input placeholder={t('e.g. my-worker')} />
                    </Form.Item>

                    <Form.Item
                        name="command"
                        label={t('Start command')}
                        rules={[{ required: true, message: t('Please enter a startup command') }]}
                    >
                        <TextArea
                            rows={3}
                            placeholder={t('e.g. /usr/bin/node /var/www/html/worker.js')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="working_dir"
                        label={t('Working directory')}
                        initialValue="/var/www/html"
                    >
                        <Input placeholder="/var/www/html" />
                    </Form.Item>

                    <Form.Item
                        name="user"
                        label={t('Run as user')}
                        initialValue="hostmanager"
                    >
                        <Select>
                            <Option value="hostmanager">hostmanager</Option>
                            <Option value="www">www</Option>
                            <Option value="root">root</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="auto_restart"
                        label={t('Automatic restart')}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch checkedChildren={t('Enable')} unCheckedChildren={t('Close')} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ServiceManager;
